import * as React from 'react';
import styled from '@emotion/styled';
import { transparentize } from 'polished';
import get from 'lodash/get';

import { Icon } from '../icons';

type Props = {
  ariaLabel?: string,
  innerRef?: HTMLInputElement,
  name: string,
  label?: string,
  value: string,
  id: string,
  input?: mixed,
  className: ?string,
  withBorder?: boolean,
  disabled?: boolean,
  screenReaderVisible?: boolean,
};

const Checkbox = ({
  ariaLabel,
  innerRef,
  name,
  label,
  value,
  id,
  input,
  className,
  withBorder,
  disabled,
  screenReaderVisible,
  ...rest
}: Props) => (
  <CheckboxWrapper
    className={className}
    htmlFor={`checkbox-${value || get(input, 'value') || id}`}
    withBorder={withBorder}
    active={get(input, 'checked')}
    disabled={disabled}
  >
    <CheckboxInput
      aria-label={ariaLabel}
      id={`checkbox-${value || get(input, 'value') || id}`}
      ref={innerRef}
      type="checkbox"
      name={name}
      disabled={disabled}
      screenReaderVisible={screenReaderVisible}
      {...input}
      {...rest}
    />
    <CheckedState disabled={disabled}>
      <StyledIcon name="Check" />
    </CheckedState>
    <UncheckedState disabled={disabled} />
    {label && <Label aria-hidden={screenReaderVisible}>{label}</Label>}
  </CheckboxWrapper>
);

const StyledIcon = styled(Icon)`
  fill: ${({ theme }) => theme.color.white};
`;

const DefaultCheckbox = styled('div')`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 20px;
  height: 20px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  flex: 0 0 20px;

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    width: 24px;
    height: 24px;
    flex: 0 0 24px;
    font-size: ${({ theme }) => theme.fontSize.md};
  }
`;

const UncheckedState = styled(DefaultCheckbox)`
  border: ${({ theme }) => `2px solid ${theme.color.grey}`};
  background: ${({ theme }) => theme.color.white};

  ${({ theme, disabled }) =>
    disabled &&
    `
      background: ${transparentize(0.4, theme.color.lightGrey)};
      border-color: ${transparentize(0.4, theme.color.grey)};
    `};
`;

const CheckedState = styled(DefaultCheckbox)`
  display: none;
  border: ${({ theme }) => theme.stroke.brand};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  background: ${({ theme }) => theme.color.brand};

  ${({ theme, disabled }) =>
    disabled &&
    `
    background: ${theme.color.grey};
    border-color: ${theme.color.grey};
  `};
`;

const CheckboxWrapper = styled('label')`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({ theme }) => theme.color.black};
  transition: ${({ theme }) => theme.motion.transition};

  &:hover {
    ${UncheckedState} {
      border: ${({ theme, disabled }) => !disabled && `2px solid ${theme.color.brand}`};
    }
  }

  ${({ withBorder, theme, active }) =>
    withBorder &&
    `
    padding: 10px 12px;
    border: ${theme.stroke.light};
    border-radius: ${theme.borderRadius.medium};
    ${active && `color: ${theme.color.brand};`}
  `}
`;

const Label = styled('div')`
  font-size: ${({ theme }) => theme.fontSize.md};
  margin-left: 8px;
`;

const CheckboxInput = styled('input')`
  ${({ screenReaderVisible }) => (screenReaderVisible ? visuallyHiddenStyle : 'display: none;')}

  &:checked + ${CheckedState} {
    display: flex;
  }
  &:checked ~ ${UncheckedState} {
    display: none;
  }
`;

const visuallyHiddenStyle = `
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

Checkbox.defaultProps = {
  className: null,
  disabled: false,
};

export default Checkbox;
